import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { ClientMark } from '@/components/molecules';
import { type Client } from '@/content/clients';

/**
 * The mark's own band. Shorter than the home tile's 84/104px because this card
 * sits in a grid of the whole roster rather than a row of nine: the whole point
 * of the page is that a reader can take in a sector at a glance, and a taller
 * card means fewer of them on screen at once.
 */
const MARK_AREA_H = 72;

/**
 * One organisation on the /clients grid.
 *
 * The white plate is the same decision as the home tile's and for the same
 * reason: institutional logos are drawn to sit on white, most of this roster
 * is dark ink on transparency, and a card that followed the page into dark
 * mode would erase half the grid.
 *
 * What it does *not* repeat from the home tile:
 *
 * - **No sector.** Every card sits under a heading that names its sector, so
 *   a tag on the card would be the same word printed twice in one eyeful.
 * - **No grayscale-to-colour hover.** On the home page that muting stops a
 *   dozen brand palettes from becoming a patchwork and gives the cursor
 *   something to reward. Here there is no motion to stop and the whole roster of
 *   cards to scan, so a grid that is grey until pointed at is a grid a reader
 *   has to sweep to read. Logos are shown as they are.
 * - **No tooltip.** The name is not clamped here — the card grows to fit it —
 *   so there is nothing left for a tooltip to add.
 *
 * It is not a link, and nothing on it is focusable. These are other people's
 * organisations: there is no page of ours to send a reader to, and a card that
 * looked clickable would be a promise the site cannot keep.
 */
export function ClientCard({ client }: { client: Client }) {
  return (
    <Box
      component="li"
      sx={{
        listStyle: 'none',
        display: 'grid',
        gridTemplateRows: `${MARK_AREA_H}px auto`,
        gap: 1,
        p: 2,
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'common.white',
        color: 'grey.900',
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          placeItems: 'center',
          px: 0.5,
          minWidth: 0,
          // `ClientMark` sizes artwork to `CLIENT_LOGO_BOX`, which is 140px
          // wide — the home marquee's tile is 204px and affords it. A card in
          // this grid is about 170px, so a wide wordmark (DTB's is the widest)
          // ran past the card's edge. Capping the width here rather than
          // shrinking the shared box keeps the marquee at the size it was
          // tuned for, and the mark is already `object-fit: contain`, so it
          // letterboxes down instead of distorting.
          '& img': { maxWidth: '100%' },
        }}
      >
        <ClientMark client={client} />
      </Box>

      <Typography
        component="span"
        sx={{
          fontSize: '0.8125rem',
          fontWeight: 600,
          lineHeight: 1.3,
          letterSpacing: '-0.005em',
          color: 'grey.900',
          // The long names are the whole reason this is a grid and not a row:
          // "Ministry of Lands, Housing & Urban Development" is allowed all
          // the lines it needs, and the row it is in grows with it.
          hyphens: 'auto',
        }}
      >
        {client.name}
      </Typography>
    </Box>
  );
}
